import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Loader2, BookOpen, ArrowRight, CheckCircle2, FileText } from "lucide-react";

export function ManuscriptLanding() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: projects } = await api.get("/projects");
        const withMs = await Promise.all(
          projects.map(async (p) => {
            try {
              const { data } = await api.get(`/projects/${p.id}/manuscript`);
              return { ...p, manuscript: data.active };
            } catch {
              return { ...p, manuscript: null };
            }
          })
        );
        setRows(withMs);
      } catch {
        setRows([]);
      }
    })();
  }, []);

  return (
    <div>
      <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Phase 2 · Manuscript</div>
      <h1 className="font-serif text-4xl tracking-tight text-slate-100">Manuscript workspace</h1>
      <p className="mt-2 text-sm text-slate-400">Choose a production to import and inspect its manuscript.</p>

      {rows === null ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-10 flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-[#12161f]/40 py-24 text-center">
          <BookOpen className="mb-3 h-8 w-8 text-amber-400" />
          <p className="text-slate-400">Create a production first, then import its manuscript here.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {rows.map((p, i) => (
            <motion.button
              key={p.id}
              data-testid={`manuscript-project-${p.id}`}
              onClick={() => navigate(`/studio/projects/${p.id}/manuscript`)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="group flex items-center justify-between rounded-xl border border-border bg-[#12161f] p-5 text-left transition-all hover:border-amber-500/40"
            >
              <div>
                <h3 className="font-serif text-xl text-slate-100">{p.title}</h3>
                {p.manuscript ? (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {p.manuscript.word_count.toLocaleString()} words · {p.manuscript.paragraph_count} paragraphs · v{p.manuscript.version_number}
                  </p>
                ) : (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                    <FileText className="h-3.5 w-3.5" /> No manuscript imported yet
                  </p>
                )}
              </div>
              <ArrowRight className="h-4 w-4 text-slate-600 transition-transform group-hover:translate-x-1 group-hover:text-amber-400" />
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}
